/**
 * React Hook for Loading Encrypted GLTF Models
 * 
 * A React Three Fiber hook that handles fetching, decrypting, and loading
 * encrypted GLB/GLTF files with automatic fallback for non-HTTPS environments.
 * 
 * @example
 * import { useEncryptedGLTF } from './useEncryptedGLTF'
 * 
 * function MyModel() {
 *   const { gltf, loading, error } = useEncryptedGLTF({
 *     encryptedPath: '/models/encrypted/abc123',
 *     encryptionKey: import.meta.env.VITE_GLB_KEY
 *   })
 *   
 *   if (loading) return <Html>Loading...</Html>
 *   if (error) return <Html>Error!</Html>
 *   return <primitive object={gltf.scene} />
 * }
 */

import { useState, useEffect } from 'react'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader'
import { decryptGLB, isSecureContext } from './decrypt-browser.js'

/**
 * Hook to load encrypted GLTF models
 * @param {Object} options - Configuration options
 * @param {string} options.encryptedPath - Path to encrypted GLB file
 * @param {string} options.encryptionKey - Encryption key (64-char hex string)
 * @param {string} [options.fallbackPath] - Unencrypted model path for non-HTTPS
 * @param {Function} [options.onProgress] - Progress callback (xhr) => void
 * @param {Function} [options.onError] - Error callback (error) => void
 * @returns {Object} - { gltf, loading, error }
 */
export function useEncryptedGLTF(options) {
  const {
    encryptedPath,
    encryptionKey,
    fallbackPath,
    onProgress,
    onError
  } = options

  const [gltf, setGltf] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let mounted = true
    let blobUrl = null

    async function loadEncryptedModel() {
      try {
        // Check if we're in a secure context (required for crypto.subtle)
        const secure = isSecureContext()

        // If not secure context and fallback provided, load unencrypted
        if (!secure && fallbackPath) {
          console.warn('⚠️ Not a secure context, loading fallback model')
          const loader = new GLTFLoader()
          
          // Setup DRACOLoader
          const dracoLoader = new DRACOLoader()
          dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/')
          loader.setDRACOLoader(dracoLoader)
          
          loader.load(
            fallbackPath,
            (loadedGltf) => {
              if (mounted) {
                setGltf(loadedGltf)
                setLoading(false)
              }
              dracoLoader.dispose()
            },
            onProgress,
            (err) => {
              console.error('❌ GLTFLoader error (fallback):', err)
              dracoLoader.dispose()
              if (mounted) {
                const errorObj = new Error(`Failed to load fallback model: ${err.message}`)
                setError(errorObj)
                setLoading(false)
                onError?.(errorObj)
              }
            }
          )
          return
        }

        // Validate required parameters
        if (!encryptionKey) {
          throw new Error('encryptionKey is required')
        }

        if (!encryptedPath) {
          throw new Error('encryptedPath is required')
        }

        // Fetch encrypted file
        const response = await fetch(encryptedPath)
        if (!response.ok) {
          const errorText = await response.text()
          console.error('❌ Fetch failed:', errorText)
          throw new Error(`Failed to fetch encrypted model: ${response.status} ${response.statusText}`)
        }

        // Get encrypted data
        const encryptedData = await response.arrayBuffer()

        // Decrypt
        const decryptedData = await decryptGLB(encryptedData, encryptionKey)

        // Create blob URL from decrypted data
        const blob = new Blob([decryptedData], { type: 'model/gltf-binary' })
        blobUrl = URL.createObjectURL(blob)

        // Load with GLTFLoader
        const loader = new GLTFLoader()
        
        // Setup DRACOLoader for compressed models
        const dracoLoader = new DRACOLoader()
        dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/')
        loader.setDRACOLoader(dracoLoader)
        
        loader.load(
          blobUrl,
          (loadedGltf) => {
            if (mounted) {
              setGltf(loadedGltf)
              setLoading(false)
            }
            dracoLoader.dispose()
          },
          onProgress,
          (err) => {
            console.error('❌ GLTFLoader error:', err)
            dracoLoader.dispose()
            if (mounted) {
              const errorObj = new Error(`Failed to load GLTF: ${err.message}`)
              setError(errorObj)
              setLoading(false)
              onError?.(errorObj)
            }
          }
        )
      } catch (err) {
        console.error('❌ Failed to load encrypted model:', err)
        if (mounted) {
          setError(err)
          setLoading(false)
          onError?.(err)
        }
      }
    }

    loadEncryptedModel()

    // Cleanup
    return () => {
      mounted = false
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl)
      }
    }
  }, [encryptedPath, encryptionKey, fallbackPath, onProgress, onError])

  return { gltf, loading, error }
}

/**
 * Preload an encrypted GLTF model
 * Useful for preloading assets before they're needed
 * @param {string} encryptedPath - Path to encrypted GLB file
 * @param {string} encryptionKey - Encryption key
 * @returns {Promise<Object>} - Loaded GLTF object
 */
export async function preloadEncryptedGLTF(encryptedPath, encryptionKey) {
  return new Promise((resolve, reject) => {
    let blobUrl = null

    fetch(encryptedPath)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to fetch: ${response.status}`)
        }
        return response.arrayBuffer()
      })
      .then(encryptedData => decryptGLB(encryptedData, encryptionKey))
      .then(decryptedData => {
        const blob = new Blob([decryptedData], { type: 'model/gltf-binary' })
        blobUrl = URL.createObjectURL(blob)

        const loader = new GLTFLoader()
        loader.load(
          blobUrl,
          (gltf) => {
            URL.revokeObjectURL(blobUrl)
            resolve(gltf)
          },
          undefined,
          (error) => {
            URL.revokeObjectURL(blobUrl)
            reject(error)
          }
        )
      })
      .catch(reject)
  })
}
